<script setup lang="ts">
import { RouterLink } from "vue-router";
import { ref, onMounted, watch, computed, onUnmounted, inject } from "vue";
import type { Ref } from "vue";
import { APIURL } from "@/defaults";
import SidebarLink from "./SidebarLink.vue";
import type { HoldingData, PriceDataObject, StockData, HoldingDataObject, FundDetails, StockDataObject, WsPubSub } from "@/interfaces/interfaces";
import type { SignalsData } from "@/interfaces/interfaces.js"
import { formatPercent, formatNumber, formatRelativeTime } from "@/helpers/formatters";

import AutoComplete, { type AutoCompleteCompleteEvent } from 'primevue/autocomplete';

const stocks_injected = inject('stocks') as Ref<StockDataObject | null>;
const wsPubSub = inject<WsPubSub>('wsPubSub');

import Fuse from "fuse.js";

import { useAuth } from "@/composables/useAuth";

const { userData } = useAuth()

const props = defineProps<{
    fund_id: string;
}>();

const prices = inject('prices') as Ref<PriceDataObject | null>;

const stocks: Ref<StockData[]> = ref([]);
const fund: Ref<null | FundDetails> = ref(null);
const holdings = inject('holdings') as Ref<HoldingDataObject | null>;
const signals = inject('signals') as Ref<SignalsData | null>;

const searchSuggestionOptions: Ref<string[]> = ref([]);
const searchSuggestions: Ref<string[]> = ref([]);
const searchString: Ref<string> = ref("");


const fuse = new Fuse(stocks.value, {
    minMatchCharLength: 2,
    threshold: 0.0,
    keys: ["name", "tags", "geo_region", "lead_analyst", "growth_phase", "growth_category", "country_of_issue_search"],
});

const updateData = () => {
    fetch(`${APIURL}/fund/${props.fund_id}`, { credentials: "include" })
        .then((r) => r.json())
        .then((r) => {
            fund.value = r;
        });
};

let updateInterval: ReturnType<typeof setInterval>;

const handleWsMessage = (data: any) => {
    if (data.event === 'prices_updated') {
        console.log("Sidebar: Update due to WS event");
        updateData();
    }
};

watch(
    () => props.fund_id,
    () => {
        fund.value = null;
        updateData();
    },
);

watch(stocks_injected, (newStocks) => {
    if (newStocks) {
        stocks.value = Object.values(newStocks).sort((a: StockData, b: StockData) => {
            if (a.name.indexOf("Bankkonto") >= 0) {
                if (b.name.indexOf("Bankkonto") >= 0) {
                    return a.name.localeCompare(b.name);
                }
                return -1;
            }
            return a.name.localeCompare(b.name);
        });
        fuse.setCollection(stocks.value.map(s => {
            return {
                ...s,
                country_of_issue_search: s.country_of_issue ? `Country ${s.country_of_issue}` : null
            }
        }));

        // update search suggestion options
        const filterTags = new Set();
        const filterPersons = new Set();
        const filterCategories = new Set();
        const filterPhases = new Set();
        const filterCountries = new Set();
        stocks.value.forEach(s => {
            if(s.geo_region && s.geo_region !== 'Cash') {
                filterTags.add(s.geo_region);
            }
            if(s.lead_analyst) {
                filterPersons.add(s.lead_analyst.charAt(0).toUpperCase() + s.lead_analyst.slice(1));
            }
            if(s.growth_category) {
                filterCategories.add(s.growth_category);
            }
            if(s.growth_phase) {
                filterCategories.add(s.growth_phase);
            }
            if(s.country_of_issue) {
                filterCountries.add(`Country ${s.country_of_issue}`);
            }
            if(s.tags) {
                s.tags.forEach(tag => {
                    filterTags.add(tag);
                })
            }
        });
        // filterTags.add('Crypto');

        const filterCountriesArray = Array.from(filterCountries).sort()

        searchSuggestionOptions.value =
            (Array.from(filterTags) as string[])
            .concat(Array.from(filterPersons) as string[])
            .concat(Array.from(filterCategories) as string[])
            .concat(Array.from(filterPhases) as string[])
            .concat(Array.from(filterCountriesArray) as string[]);
    }
}, { immediate: true });

onMounted(() => {
    updateData();
    // Subscribe to websocket messages
    if (wsPubSub) {
        wsPubSub.subscribe(handleWsMessage);
    }
    // Fallback interval every 15 minutes
    updateInterval = setInterval(updateData, 60e3 * 15);
});

onUnmounted(() => {
    if (wsPubSub) {
        wsPubSub.unsubscribe(handleWsMessage);
    }
    clearInterval(updateInterval);
});

interface SidebarStocks {
    portfolio: StockData[];
    watchlist: StockData[];
}

interface SidebarCash {
    weight: number;
    value_eur: number;
}

const sidebarCash: Ref<SidebarCash | null> = computed(() => {
    if (!holdings.value) {
        return null;
    }

    const totalPortfolioValue = holdings.value.total_value_eur;
    const cashHoldings = Object.values(holdings.value.values).filter((holding) => holding.type == 'BAR' || holding.type == 'VERBINDLICHKEITEN' || holding.type == 'FORDERUNGEN');

    const cashValueTotal = cashHoldings.reduce((total: number, holding: HoldingData) => total + holding.booked_data.value_eur, 0);
    const cashWeightTotal = cashHoldings.reduce((total: number, holding: HoldingData) => total + holding.booked_data.fund_proportion, 0)

    return {
        weight: cashWeightTotal,
        value_eur: cashValueTotal,
    };
});

const sidebarStocks: Ref<SidebarStocks> = computed(() => {
    if (!stocks.value) {
        return { portfolio: [], watchlist: [] };
    }
    if (!holdings.value) {
        return { portfolio: [], watchlist: [] };
    }

    let stocks_list = stocks.value.slice();

    // filter for tags
    if (searchString && searchString.value.length >= 2) {
        const results = fuse.search(searchString.value);
        stocks_list = results.map((result) => result.item);
    }

    const holdings_list: HoldingData[] = Object.values(holdings.value.values);
    holdings_list.sort((a, b) => b.booked_data.value_eur - a.booked_data.value_eur);
    const isins = holdings_list.map((h) => h.isin);
    const isin_order: Record<string, number> = {};
    isins.forEach((isin, index) => {
        isin_order[isin] = index;
    });

    let stocks_sorted = stocks_list.slice().sort((a, b) => {
        const x = a.isin in isin_order ? isin_order[a.isin] : a.name.charCodeAt(0) + 1e3;
        const y = b.isin in isin_order ? isin_order[b.isin] : b.name.charCodeAt(0) + 1e3;
        return x - y;
    });

    stocks_sorted = stocks_sorted.filter((s) => {
        return s.isin.match(/^[A-Z]{2}[0-9A-Z]{10}$/);
    });

    return {
        portfolio: stocks_sorted.filter((s) => {
            return s.isin in isin_order;
        }),
        watchlist: stocks_sorted.filter((s) => {
            return !(s.isin in isin_order);
        }),
    };
});

const filteredPortfolioWeight = computed(() => {
    if (!holdings.value || !sidebarStocks.value.portfolio.length) return 0;

    return sidebarStocks.value.portfolio.reduce((sum, stock) => {
        const holding = holdings.value?.values[stock.isin];
        return sum + (holding?.booked_data.fund_proportion || 0);
    }, 0);
});


const searchCompleteHandler = (e: AutoCompleteCompleteEvent) => {
    const _items = [...searchSuggestionOptions.value];

    searchSuggestions.value = e.query ? _items.filter(i => i.toLowerCase().startsWith(e.query.toLowerCase())) : _items;
};

const searchClearedHandler = () => {
    searchSuggestions.value = [...searchSuggestionOptions.value];
}

const clearSearch = () => {
    searchString.value = "";
    searchSuggestions.value = [...searchSuggestionOptions.value];
}

</script>

<template>
    <div class="app-sidebar">
        <nav>
            <RouterLink to="/" class="sidebar-link stock-link fund-link">
                <span class="stock-link-name">Home <span class="axxion-data-check" v-if="fund?.axxion_reported_today" title="Axxion has reported today"></span><span class="axxion-data-check axxion-data-check-pending" v-else-if="fund && !fund.axxion_reported_today" title="Axxion has not (yet) reported today"></span></span>
                <span class="stock-link-isin">
                    <template v-if="fund">{{fund.isin}}</template>
                    <template v-else>&nbsp;</template>
                </span>
                <div class="stock-link-price">
                    <div class="stock-link-price-current">
                        <template v-if="fund">{{formatNumber(fund.price_intraday, 2)}}</template>
                        <template v-else>&nbsp;</template>
                    </div>
                    <div v-if="fund" class="stock-link-price-change" :class="{'--negative': fund.performance_intraday.total < 0, '--positive': fund.performance_intraday.total > 0}">{{formatPercent(fund.performance_intraday.total, 2, true)}}</div>
                    <div v-else class="stock-link-price-change">&nbsp;</div>
                </div>
            </RouterLink>

            <div class="sidebar-nav-container">
                <!-- Always visible links -->
                <div class="sidebar-nav-visible">
                    <RouterLink to="/holdings" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/all.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">All Holdings</p>
                    </RouterLink>

                    <RouterLink to="/feed" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/feed.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Feed</p>
                    </RouterLink>

                    <RouterLink to="/signals" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/flag.svg" alt="" width="20" height="20" />
                      <p class="sidebar-signals"><span class="sidebar-link-title-with-shortcut">Signals</span> <span class="sidebar-signals-indicator" v-if="signals">{{ signals.buy.length }} / {{signals.sell.length}}</span></p>
                    </RouterLink>

                    <RouterLink to="/cashflow" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/cash.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Inflow / Outflow</p>
                    </RouterLink>
                </div>

                <!-- Hidden links that show on hover -->
                <div class="sidebar-nav-hidden">
                    <RouterLink to="/risk" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/risk.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Risk Analysis</p>
                    </RouterLink>

                    <RouterLink to="/calendar" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/calendar.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title">Calendar</p>
                    </RouterLink>

                    <RouterLink to="/returncontributions" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/cash.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Contributions</p>
                    </RouterLink>

                    <RouterLink to="/macro" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/globe.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Macro</p>
                    </RouterLink>

                    <RouterLink to="/comparison" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/comparison.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Peer Analysis</p>
                    </RouterLink>

                    <RouterLink to="/scenarios" class="sidebar-link sidebar-link--nav">
                      <img src="/icons/globe.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title">Scenarios</p>
                    </RouterLink>

                    <RouterLink to="/orders" class="sidebar-link sidebar-link--nav" v-if="userData?.permissions.orders">
                      <img src="/icons/cashflow.svg" alt="" width="20" height="20" />
                      <p class="sidebar-link-title-with-shortcut">Orders</p>
                    </RouterLink>

                    <RouterLink to="/management" class="sidebar-link sidebar-link--nav" v-if="userData?.permissions.admin">
                      <img src="/icons/all.svg" alt="" width="20" height="20" />
                      <p class="">Management</p>
                    </RouterLink>
                </div>
            </div>

            <hr />

            <div class="app-sidebar-stocks">
              <div class="sidebar-search">
                <div class="search-container">
                  <AutoComplete v-model="searchString" :suggestions="searchSuggestions" dropdown @complete="searchCompleteHandler" @clear="searchClearedHandler" />
                  <button v-if="searchString && searchString.length > 0" @click="clearSearch" class="search-clear-btn" type="button" title="Clear search">
                    ×
                  </button>
                </div>
                <div v-if="searchString && searchString.length >= 2" class="filtered-weight">
                    Portfolio weight: {{formatPercent(filteredPortfolioWeight, 2)}}
                </div>
              </div>
              <div v-if="!searchString || searchString.length < 2" class="sidebar-link stock-link cash-link" :title="sidebarCash ? `${formatNumber(sidebarCash.value_eur)} EUR` : ''">
                <img src="/icons/euro.svg" alt="" class="stock-link-icon" />
                <span class="stock-link-name">Cash</span>
                <div class="stock-link-price" v-if="sidebarCash">
                    <div class="stock-link-price-current">
                        <template v-if="sidebarCash.weight">{{formatPercent(sidebarCash.weight, 2)}}</template>
                        <br>
                        <template v-if="sidebarCash.value_eur">{{formatNumber(sidebarCash.value_eur)}}</template>
                    </div>
                </div>
              </div>
              <SidebarLink v-for="stock in sidebarStocks.portfolio" :key="stock.isin" :data="stock" :price-data="prices && prices[stock.isin] ? prices[stock.isin] : null" :holding-data="holdings && holdings.values[stock.isin] ? holdings.values[stock.isin] : null" />
              <!--
              <hr />
              <SidebarLink v-for="stock in sidebarStocks.watchlist" :key="stock.isin" :data="stock" :price-data="prices && prices[stock.isin] ? prices[stock.isin] : null" :holding-data="holdings && holdings.values[stock.isin] ? holdings.values[stock.isin] : null" />
              -->
            </div>
        </nav>
    </div>
</template>

<style scoped>
.app-sidebar {
  height: 100%;
  overflow: hidden;
  background-color: #fff;
  box-shadow: 0 0 10px rgba(0, 20, 40, 0.2);
  display: flex;
  flex-direction: column;
}
.app-sidebar nav {
    height: 100%;
    display: flex;
    flex-direction: column;
}
.app-sidebar-stocks {
    overflow: auto;
    flex: 1;
}

.sidebar-nav-container {
  position: relative;
}

.sidebar-nav-visible {
  z-index: 2;
  position: relative;
  background-color: #fff;
}

.sidebar-nav-hidden {
  display: none;
  z-index: 1;
  background-color: #fff;
}

.sidebar-nav-container:hover .sidebar-nav-hidden,
.sidebar-nav-container:focus-within .sidebar-nav-hidden {
  display: block;
}

.sidebar-search {
  padding: 10px 20px;
}
.sidebar-search input {
  width: 100%;
  font: inherit;
  background: rgba(0,0,0,0.1);
  border: 0 none;
  border-radius: 4px;
  color: #000;
  padding: 6px 6px;
  font-size: 14px;
  display: none;
}
.sidebar-search .p-autocomplete {
    width: 100%;
}

.search-container {
  position: relative;
  display: flex;
  align-items: center;
}

.search-clear-btn {
  position: absolute;
  right: 45px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 3px;
  line-height: 1;
  z-index: 5;
}

.search-clear-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #000;
}

/* Base class for all status indicator dots */
.axxion-data-check {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    vertical-align: middle;
    cursor: help;
    background: rgb(101,196,102); /* Active/reported state */
}

/* Modifier for pending state */
.axxion-data-check-pending {
    background: #d3d3d3; /* Pending/not reported state */
}
.sidebar-signals {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}
.sidebar-signals span {
    display: block;
}
.sidebar-signals-indicator {
    background: rgba(0,0,0,0.2);
    color: #fff;
    border-radius: 3px;
    padding: 3px 6px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    vertical-align: middle;
}

.sidebar-link-title-with-shortcut::first-letter {
    font-weight: 700;
}

.filtered-weight {
    font-size: 12px;
    color: #666;
    margin-top: 4px;
    padding: 0 6px;
}
</style>
